import * as $ from 'jquery';
import { compositeSubscription } from 'common/barrels/core';
import { Observable, Subscription, Subject, BehaviorSubject } from 'common/barrels/rx';
import { div, ul, li, a, icon } from 'common/barrels/dom';
import { bind, Binding } from 'common/dom/binding';
import { unobtrusiveScroll } from 'common/rxui/scroll';
import { windowResized } from 'common/rxui/core';
import { Map, Set } from 'immutable';
import 'common/styles/tab.less';

export interface TabDetails {
    title: string | Observable<string>;
    content: () => JQuery;
    active?: BehaviorSubject<boolean>;
    closeable?: boolean;
    onBeforeClose?: () => Observable<boolean>;
    subscriptions?: Subscription;
}

export function tab(value: Binding<TabDetails[]>, enableReorder: boolean = false) {
    const $container = div('studio-tab').append(div('studio-tab-buffer'));

    const $headerContainer = div().appendTo($container);

    const $content = div('studio-tab-content').attr('tabindex', 0).appendTo($container);

    // think I'm gonna be sick... but what should I use when I *do* want a mutable collection? It's rare, but this is one of the cases...
    const contentCache = Map<TabDetails, JQuery>().asMutable();

    const tabsInternal = new Subject<TabDetails[]>();

    bind(value, x => build($headerContainer, x, $content, contentCache, tabsInternal, enableReorder), () => tabsInternal);

    const subscription = value instanceof Observable
        ? value.pairwise().subscribe(([oldItem, newItem]) => {
            const set = Set<TabDetails>(newItem);
            const missing = oldItem.filter(x => !set.has(x));

            missing.forEach(x => {
                if (x.subscriptions)
                    x.subscriptions.unsubscribe();

                contentCache.get(x).remove();
                contentCache.remove(x);
            });
        })
        : new Subscription();

    $container.owns(subscription);

    return $container;
}

function build($headerContainer: JQuery, value: TabDetails[], $content: JQuery, contentCache: Map<TabDetails, JQuery>, tabsInternal: Subject<TabDetails[]>, enableReorder: boolean) {
    value.forEach(x => {
        if (!x.active)
            x.active = new BehaviorSubject<boolean>(false);
    });

    const removeTab = (t: TabDetails) => {
        tabsInternal.next(value.filter(x => x !== t));
    };

    const headerItems = value.map(x => ({ item: x, element: buildStripItem(x, removeTab) }));

    const $ul = ul('studio-tab-header').append(headerItems.map(x => x.element));

    $headerContainer.empty().append($ul);

    const moveTabBefore = (source: JQuery, target: JQuery) => {
        const elements = headerItems.map(x => x.element.get(0));
        const items = headerItems.map(x => x.item);

        const sourceIndex = elements.indexOf(source.get(0));
        const targetIndex = elements.indexOf(target.get(0));

        const otherItems = items.filter((x, i) => i !== sourceIndex);

        const newItems = otherItems.filter((x, i) => i < targetIndex).concat([items[sourceIndex]]).concat(otherItems.filter((x, i) => i >= targetIndex));

        tabsInternal.next(newItems);
    };    

    const activeTab = Observable.of(...value.map(x => x.active.map(y => ({ active: y, tab: x }))))
        .mergeAll()
        .startWith({ active: true, tab: null })
        .filter(x => x.active)
        .map(x => x.tab)
        .distinctUntilChanged();

    const subscriptions = compositeSubscription(
        enableReorder ? wireDragBehaviour($headerContainer, moveTabBefore) : Subscription.EMPTY,

        activeTab
            .pairwise()
            .subscribe(([oldItem, newItem]) => {
                // this is causing issues, and I'm not exactly sure what should be done with it.
                // arguably, simply displaying a different tab should not request focus.
                //$content.focus();

                $content.children().hide();

                if (oldItem && oldItem.active)
                    oldItem.active.next(false);

                if (newItem) {
                    if (!contentCache.has(newItem)) {
                        contentCache.set(newItem, newItem.content());
                        $content.append(contentCache.get(newItem));
                    }

                    contentCache.get(newItem).show();

                    if (newItem.active)
                        newItem.active.next(true);
                }
            }) 
    );

    unobtrusiveScroll($ul, 'horizontal', windowResized().startWith({}).delay(0));
    $ul.scrollLeft($ul[0].scrollWidth);

    $ul.owns(subscriptions);

    if (value.length > 0 && value.every(x => !x.active.getValue()))
        value[0].active.next(true);
}

function buildStripItem(item: TabDetails, removeTab: (t: TabDetails) => void) {
    const $header = li('studio-tab-header-item noselect')
        .append(a().bindText(item.title))
        .append(item.closeable
            ? icon('times', 'Close', 'studio-tab-close')
                .clicks(o => o.switchMap(() => item.onBeforeClose ? item.onBeforeClose() : Observable.of(true)).filter(x => x)
                    .subscribe(() => removeTab(item)))
            : $()
        )
        .on('mousedown', () => item.active.next(true));

    const subscription = item.active.subscribe(x => $header.toggleClass('studio-tab-header-active', x));

    $header.owns(subscription);

    return $header;
}

function wireDragBehaviour($container: JQuery, insertBefore: (source: JQuery, destination: JQuery) => void): Subscription {
    const dragThreshold = 5;
    const down = Observable.fromEvent<MouseEvent>($container.find('ul>li'), 'mousedown').map(e => ({ element: $(e.currentTarget), mouseX: e.pageX, mouseY: e.pageY }));
    const up = Observable.fromEvent<MouseEvent>($(document), 'mouseup');
    const move = Observable.fromEvent<MouseEvent>($(document), 'mousemove').map(e => ({ mouseX: e.pageX, mouseY: e.pageY }));

    const dragStart = down.flatMap(x => move
        .takeUntil(up)
        .filter(p => Math.abs(p.mouseX - x.mouseX) > dragThreshold)
        .take(1)
        .map(() => x)
    );

    const dragMove = dragStart.flatMap(d => move
        .takeUntil(up)
        .startWith(d)
        .pairwise()
        .map(p => ({ deltaX: p[1].mouseX - p[0].mouseX, deltaY: p[1].mouseY - p[0].mouseY, mouseX: p[1].mouseX, mouseY: p[1].mouseY }))
        .scan((a, x) => ({ x: a.x + x.deltaX, y: a.y, mouseX: x.mouseX, mouseY: x.mouseY }), ({ x: d.element.offset().left, y: d.element.offset().top, mouseX: d.mouseX, mouseY: d.mouseY }))
    );

    const dropTarget = dragMove
        .map(x => document.elementFromPoint(x.mouseX, x.y))
        .map(x => $.contains($container.get(0), x) ? $(x).closest('li') : null)
        .map(x => !x ? null : x.length === 0 ? null : x)
        .distinctUntilChanged()
        ;

    const dragEnd = dragStart.switchMap(s => up.take(1).map(() => s));

    const tabDropped = dragEnd.withLatestFrom(dropTarget, (x, y) => ({ source: x.element, destination: y })).filter(x => x.destination !== null);

    let ghost: JQuery = null;

    return compositeSubscription(
        dragStart.subscribe(c => ghost = createGhost(c.element)),

        dragMove.subscribe(x => ghost.offset({ left: x.x, top: x.y })),

        dropTarget.pairwise().subscribe(([o, n]) => {
            (o || $()).removeClass('studio-tab-header-drop-indicator');
            (n || $()).addClass('studio-tab-header-drop-indicator');
        }),

        tabDropped.subscribe(x => {
            x.destination.removeClass('studio-tab-header-drop-indicator');
            insertBefore(x.source, x.destination);
        }),

        dragEnd.subscribe(() => ghost.remove()),

        new Subscription(() => ghost.remove())
    );
}

function createGhost($source: JQuery) {
    const startingOffset = $source.offset();

    return $source
        .clone()
        .addClass('studio-tab-ghost')
        .offset({ left: startingOffset.left, top: startingOffset.top })
        .appendTo($('body'))
        ;
}